/**
 * Sign-in.
 *
 * Three fields, because Conduit is multi-tenant and the tenant is part of the identity rather than
 * something inferred from the hostname — an MSSP's engineer holds principals in several.
 *
 * Every failure renders the server's message unchanged, and the server returns an identical 401
 * for an unknown tenant, an unknown login, and a wrong password. Distinguishing them here would
 * hand back the account-enumeration oracle the backend deliberately withholds.
 *
 * After repeated failures the server demands a challenge, in two escalating kinds.
 *
 * Proof of work is solved by `api.signIn` itself, so the only thing this page does about it is say
 * so — a button that sat on "Signing in…" for several seconds would read as a hung page, and a hung
 * login page gets reloaded, which throws the work away and starts again.
 *
 * A CAPTCHA needs a widget on screen, so it does reach this page. It appears only where the
 * deployment configured a provider and only after ten failures within the hour; most installations
 * never render one. See `crates/conduit-control/src/captcha.rs` for why it is opt-in.
 */

import { useEffect, useRef, useState } from 'react';
import type { JSX, SyntheticEvent } from 'react';

import { api, ApiError } from '../shared/api/client';
import type { ChallengeDemand } from '../shared/api/client';
import { ErrorBox } from '../shared/ui/ui';
import './signin.css';

export function SignIn({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [tenant, setTenant] = useState('');
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [captcha, setCaptcha] = useState<Extract<ChallengeDemand, { kind: 'captcha' }> | null>(null);
  const widget = useRef<HTMLDivElement>(null);

  /** One attempt, carrying a CAPTCHA token if the last demand produced one. */
  async function attempt(captchaToken?: string): Promise<void> {
    setBusy(true);
    setVerifying(false);
    setError(null);
    try {
      await api.signIn(
        tenant.trim(),
        login.trim(),
        password,
        () => {
          setVerifying(true);
        },
        captchaToken,
      );
      setCaptcha(null);
      onSignedIn();
    } catch (err) {
      // A CAPTCHA demand is not an error to show — it is work to present. Anything else is.
      if (err instanceof ApiError && err.challenge?.kind === 'captcha') {
        setCaptcha(err.challenge);
      } else {
        setError(err);
      }
    } finally {
      setBusy(false);
      setVerifying(false);
    }
  }

  async function submit(e: SyntheticEvent): Promise<void> {
    e.preventDefault();
    await attempt();
  }

  // Renders the widget once a demand arrives, and resubmits with the token the user earns.
  //
  // The password is still in component state at this point, which is why the whole flow stays on
  // this page: asking somebody to retype it after solving a puzzle is how a security control gets
  // remembered as the reason people stopped using the product.
  useEffect(() => {
    if (!captcha || !widget.current) return;

    let cancelled = false;
    const container = widget.current;
    const demand = captcha;

    const run = async (): Promise<void> => {
      try {
        const { solveCaptcha } = await import('../shared/auth/captcha');
        const token = await solveCaptcha(demand.provider, demand.siteKey, container);
        if (!cancelled) await attempt(token);
      } catch (err) {
        if (!cancelled) setError(err);
      }
    };

    void run();

    // Cancelled rather than aborted: the widget's callback may still fire after the demand
    // changes, and resubmitting from a stale one would sign in against fields the user has since
    // edited.
    return (): void => {
      cancelled = true;
    };
    // `attempt` intentionally closes over the current field values — that is what a resubmit
    // needs — so it is not a dependency. The effect runs when a new demand arrives, and only then.
  }, [captcha]);

  const label = verifying ? 'Verifying…' : busy ? 'Signing in…' : 'Sign in';

  return (
    <main className="signin">
      <form className="signin__card" onSubmit={(e) => void submit(e)}>
        <div className="signin__brand">
          <div className="signin__mark">C</div>
          <div>
            <div className="signin__product">Conduit</div>
            <div className="signin__tagline">ticketed · evidenced · time-boxed</div>
          </div>
        </div>

        <ErrorBox error={error} />

        <label className="signin__label" htmlFor="tenant">
          Organization
        </label>
        <input
          id="tenant"
          className="input signin__input"
          value={tenant}
          onChange={(e) => { setTenant(e.target.value); }}
          autoComplete="organization"
          required
        />

        <label className="signin__label" htmlFor="login">
          Login
        </label>
        <input
          id="login"
          className="input signin__input"
          value={login}
          onChange={(e) => { setLogin(e.target.value); }}
          autoComplete="username"
          required
        />

        <label className="signin__label" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          className="input signin__input"
          type="password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); }}
          autoComplete="current-password"
          required
        />

        <button className="button signin__submit" type="submit" disabled={busy}>
          {label}
        </button>

        {verifying ? (
          <p className="signin__note" role="status">
            Extra verification is required after several failed attempts. This takes a few seconds
            and happens in your browser.
          </p>
        ) : null}

        {captcha ? (
          <>
            <p className="signin__note" role="status">
              Too many failed attempts. Complete the verification below to continue.
            </p>
            <div className="signin__captcha" ref={widget} />
          </>
        ) : null}
      </form>
    </main>
  );
}
